import React, { useState, useEffect } from 'react'
import {Button, FormGroup, Input, Label } from 'reactstrap';
import PropTypes from 'prop-types'
import * as Yup from 'yup';
import { Field, Form, Formik } from 'formik';
import ComponentLoader from '../ComponentLoader';
import { toastr } from 'react-redux-toastr';
import apiClient from '../../gears/apiClient';

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  description: Yup.string().required('Description is required'),
  action: Yup.string().required('Action is required'),
  active: Yup.boolean().required(),
  triggerUrl: Yup.array()
    .of(Yup.string().required('URL must be a string'))
    .min(1, 'At least one trigger URL is required')
    .required('Trigger URL is required'),
});

const CreateNewConfigForm = ({ entryData = null, closeModal, updateData }) => {
  const [configActions, setConfigActions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchAssistActions = async () => {
      setIsLoading(true);
      try {
        const response = await apiClient.get('/assist-actions');
        setConfigActions(response.actions);
      } catch (e) {
        setConfigActions([]);
      }

      setIsLoading(false);
    }

    fetchAssistActions();
  }, []);
 
  const updateOrCreateConfig = async (data) => {
    setIsLoading(true);
    const request = entryData
      ? apiClient.put(`/assist-config/${entryData.id}`, data)
      : apiClient.post('/assist-config', data)

    try {
      await request;
      updateData();
      closeModal();
      toastr.success(`${entryData ? 'Updated' : 'Created'} successfully`)
    } catch(e) {
      toastr.error(e?.error);
    }

    setIsLoading(false);
  }

  return (
    <div className="deploy-google-admin">

      {isLoading && <ComponentLoader />}

      <Formik
        initialValues={{
          name: entryData?.name || '',
          description: entryData?.description || '',
          action: entryData?.action || configActions[0],
          active: entryData?.active ?? true,
          triggerUrl: entryData?.triggerUrl || [''],
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => updateOrCreateConfig(values)}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, handleBlur, isValid, dirty, setFieldValue }) => (
          <Form id="assist-config-form" className="form">

            <FormGroup>
              <Label for="entry-name">Headline</Label>
              <Field
                id="entry-name"
                className="form-control"
                type="text"
                name="name"
                placeholder="Need help with this page?"
              />
              {errors.name && touched.name && <div className="error">{errors.name}</div>}
            </FormGroup>

            <FormGroup>
              <Label for="entry-name">Subheadline</Label>
              <Field
                id="entry-description"
                className="form-control"
                type="text"
                name="description"
                placeholder="Our AI assistant can help you navigate this page"
              />
              {errors.description && touched.description && <div className="error">{errors.description}</div>}
            </FormGroup>

            <FormGroup>
              <Label for="action">Action</Label>
              <Field
                id="entry-action"
                className="form-control"
                as="select"
                name="action"
              >
                {configActions.map((action) => (
                  <option key={action} value={action}>
                    {action}
                  </option>
                ))}
              </Field>
              {errors.action && touched.action && <div className="error">{errors.action}</div>}
            </FormGroup>

            {entryData && (<FormGroup check className="mb-3">
              <Label check>
                <Field type="checkbox" name="active" className="form-check-input" style={{ accentColor: 'black' }}/>
                {' '}Activate Notification
              </Label>
              {errors.active && touched.active && <div className="error">{errors.active}</div>}
            </FormGroup>)}

            <FormGroup>
              <Label>Trigger URLs</Label>
              {values.triggerUrl.map((url, index) => (
                <div key={index} className="d-flex align-items-center mb-1">
                  <Input
                    type="text"
                    name={`triggerUrl[${index}]`}
                    value={url}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="form-control"
                  />
                  <Button
                    type="button"
                    className="btn btn-black ml-2"
                    onClick={() => {
                      const urls = [...values.triggerUrl];
                      urls.splice(index, 1);
                      setFieldValue('triggerUrl', urls);
                    }}
                    disabled={values.triggerUrl.length === 1}
                  >
                    &times;
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                className="btn btn-black mt-2"
                onClick={() => setFieldValue('triggerUrl', [...values.triggerUrl, ''])}
              >
                Add URL
              </Button>
              {errors.triggerUrl && touched.triggerUrl && typeof errors.triggerUrl === 'string' && (
                <div className="error">{errors.triggerUrl}</div>
              )}
            </FormGroup>

            <div className="form-actions">
              <Button
                type="submit"
                disabled={!isValid || !dirty}
                className="button button-default"
                color="primary"
              >
                {entryData ? 'Update' : 'Create'}
              </Button>
              <Button
                className="ml-2 button button-default-outlined"
                color="secondary"
                onClick={() => closeModal()}
              >
                Cancel
              </Button>
            </div>
          </Form>
        )}
      </Formik>

    </div>
  )
}

CreateNewConfigForm.propTypes = {
  closeModal: PropTypes.func,
  updateData: PropTypes.func,
}

export default CreateNewConfigForm;
